import React, { useState } from 'react';
import { Check, RotateCcw, Info, AlertTriangle, CheckCircle2 } from 'lucide-react';

// ══════════════════════════════════════════════════════════════════════════
// PILOTE QUALITÉ · M1 · Installation au poste de conduite (REMC C1b)
// Œuvre pédagogique interactive :
//  · Vue 3/4 arrière du poste conducteur (siège, volant, pédalier, rétros, tableau)
//  · Checklist des 5 étapes : Ceinture → Siège → Volant → Rétros → Appuie-tête
//  · Chaque étape sélectionnée met en surbrillance la zone concernée (halo pulse)
//  · Panneau latéral : POURQUOI + PIÈGE + BON GESTE + validation avec ✓
//  · Bouton « Reset » pour recommencer la séquence
// ══════════════════════════════════════════════════════════════════════════

// ORDRE PÉDAGOGIQUE OFFICIEL (moyen mnémotechnique : « S-D-V-R-C »)
//  1. Siège           → profondeur + hauteur (position des pédales)
//  2. Dossier & appuie-tête → inclinaison + protection cervicales
//  3. Volant          → profondeur + hauteur (position des mains)
//  4. Rétroviseurs    → intérieur puis extérieurs (vision arrière)
//  5. Ceinture        → dernier geste avant démarrage
const ETAPES = [
  {
    id: 'siege',
    n: 1,
    label: 'Siège (profondeur + hauteur)',
    color: '#f59e0b',
    highlight: { x: 295, y: 340, w: 210, h: 220 },
    pourquoi: "On règle le siège EN PREMIER : la position des jambes détermine l'accès aux pédales (embrayage, frein, accélérateur). Un mauvais réglage = freinage inefficace + crampes.",
    piege: "Siège trop bas (tableau de bord masque la route) OU trop reculé (pédale de frein en bout de pied). Trop avancé = genoux collés au volant.",
    geste: "Recule / avance le siège jusqu'à ce que la pédale d'embrayage enfoncée à fond laisse la jambe légèrement pliée. Règle la hauteur pour voir 3-4 m devant le capot.",
  },
  {
    id: 'dossier',
    n: 2,
    label: 'Dossier + Appuie-tête',
    color: '#10b981',
    highlight: { x: 305, y: 110, w: 200, h: 250 },
    pourquoi: "Une fois assis, on incline le dossier pour garder les épaules au contact et on règle l'appuie-tête pour protéger les cervicales (« coup du lapin » lors d'un choc arrière).",
    piege: "Dossier trop couché (bras tendus, épaules qui décollent) OU appuie-tête trop bas (protège la nuque, pas le crâne). Distance nuque ↔ appuie-tête > 4 cm = inefficace.",
    geste: "Dossier presque droit, épaules qui touchent le dossier quand les bras sont posés sur le volant. Haut de l'appuie-tête au-dessus du sommet du crâne, distance ≤ 4 cm.",
  },
  {
    id: 'volant',
    n: 3,
    label: 'Volant (profondeur + hauteur)',
    color: '#8b5cf6',
    highlight: { x: 150, y: 300, w: 220, h: 170 },
    pourquoi: "Après le siège, on ajuste le volant pour que les mains tombent naturellement à 9 h 15, coudes légèrement pliés. Trop loin = épaules décollées, trop près = airbag dangereux.",
    piege: "Bras tendus (« comme dans les vieux films ») ou volant qui masque les compteurs. Volant trop haut = épaules qui montent en tournant.",
    geste: "Poignet posé sur le haut du volant, épaules restent au dossier → coudes pliés entre 100° et 120°. Le haut du volant ne doit PAS masquer les cadrans.",
  },
  {
    id: 'retros',
    n: 4,
    label: 'Rétros intérieur + extérieurs',
    color: '#0ea5e9',
    highlight: { x: 200, y: 60, w: 400, h: 160 },
    pourquoi: "La position de conduite est figée : on peut enfin régler les rétros. On commence par l'intérieur (le plus important, vue arrière), puis les deux extérieurs.",
    piege: "Voir sa propre carrosserie dans le rétro extérieur (perte d'espace utile). Rétro intérieur mal centré sur la lunette arrière. Régler les rétros AVANT le siège = tout à refaire.",
    geste: "Intérieur : lunette arrière entière et centrée. Extérieurs : juste 1 cm de carrosserie visible dans le coin intérieur — le reste du miroir montre la route et les angles morts.",
  },
  {
    id: 'ceinture',
    n: 5,
    label: 'Ceinture (dernier geste)',
    color: '#dc2626',
    highlight: { x: 340, y: 240, w: 120, h: 260 },
    pourquoi: "La ceinture s'attache EN DERNIER, une fois la position finale trouvée. Elle te retient au siège en cas de choc : -80 % de risque de mortalité en accident grave.",
    piege: "Pincer la ceinture sous le bras (elle passe sur la clavicule, pas sous l'aisselle). Vêtement épais entre ceinture et corps = mou de retenue. La boucler avant les réglages = ça gêne.",
    geste: "Ceinture qui glisse depuis l'épaule au milieu de la clavicule → sangle basse qui plonge sur les os du bassin. « Clic » sonore. Vérifie qu'elle n'est pas vrillée.",
  },
];

export default function InstallationPosteSvg() {
  const [selected, setSelected] = useState('siege');
  const [done, setDone] = useState(new Set());

  const current = ETAPES.find((e) => e.id === selected);
  const reset = () => { setSelected('siege'); setDone(new Set()); };
  const validate = () => {
    const next = new Set(done); next.add(selected);
    setDone(next);
    // avance auto à l'étape suivante non validée
    const idx = ETAPES.findIndex((e) => e.id === selected);
    for (let i = 1; i <= ETAPES.length; i += 1) {
      const cand = ETAPES[(idx + i) % ETAPES.length];
      if (!next.has(cand.id)) { setSelected(cand.id); break; }
    }
  };

  return (
    <div className="w-full">
      {/* Bandeau moyen mnémotechnique */}
      <div className="mb-3 rounded-xl border border-amber-500/40 bg-amber-950/25 px-3.5 py-2.5" data-testid="m1-mnemo">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/20 border border-amber-400/40 px-2 py-0.5 text-amber-200 text-[10px] font-mono-spec uppercase tracking-wider font-bold">Ordre officiel</span>
          <span className="text-white font-serif-editorial text-[15px] leading-tight">
            <b className="text-amber-300">S</b>iège →
            <b className="text-emerald-300"> D</b>ossier/Appui-tête →
            <b className="text-violet-300"> V</b>olant →
            <b className="text-sky-300"> R</b>étros →
            <b className="text-red-300"> C</b>einture
          </span>
        </div>
        <p className="mt-1 text-white/70 text-[11.5px] leading-snug">
          On règle d&apos;abord le corps (siège, dossier), puis les commandes (volant), puis la vision (rétros), et on boucle la ceinture <b>en dernier</b> pour ne pas être gêné pendant les réglages.
        </p>
      </div>

      {/* Checklist */}
      <div className="mb-4 grid grid-cols-2 sm:grid-cols-5 gap-2">
        {ETAPES.map((e) => {
          const isSel = selected === e.id;
          const isDone = done.has(e.id);
          return (
            <button
              key={e.id}
              onClick={() => setSelected(e.id)}
              data-testid={`m1-etape-${e.id}`}
              className={`h-16 rounded-xl border-2 transition text-left px-3 flex items-center gap-2.5 ${
                isSel ? 'shadow-lg' : 'hover:border-white/30'
              }`}
              style={{
                borderColor: isSel ? e.color : (isDone ? e.color + '55' : 'rgba(255,255,255,0.15)'),
                background: isSel ? e.color + '22' : (isDone ? e.color + '10' : 'rgba(255,255,255,0.02)'),
              }}
            >
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: isDone ? e.color : (isSel ? e.color : '#1e293b'), color: '#fff' }}>
                {isDone ? <Check size={16} strokeWidth={3} /> : <span className="font-black text-[13px]">{e.n}</span>}
              </div>
              <div>
                <div className="text-white text-[11.5px] font-bold leading-tight">{e.label}</div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-[1fr,340px] gap-4">
        {/* Cockpit SVG */}
        <div className="rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-b from-[#0f1a2b] to-[#0a1220]">
          <svg viewBox="0 0 800 700" className="w-full h-auto block max-h-[48vh] sm:max-h-[52vh]" role="img" aria-label="Installation poste de conduite">
            <defs>
              <linearGradient id="m1-seat" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#374151" />
                <stop offset="100%" stopColor="#1e293b" />
              </linearGradient>
              <linearGradient id="m1-dash" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#1e293b" />
                <stop offset="100%" stopColor="#0b1120" />
              </linearGradient>
              <radialGradient id="m1-halo">
                <stop offset="0%" stopColor={current.color} stopOpacity="0.4" />
                <stop offset="60%" stopColor={current.color} stopOpacity="0.15" />
                <stop offset="100%" stopColor={current.color} stopOpacity="0" />
              </radialGradient>
              <linearGradient id="m1-wheel" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3f3f46" />
                <stop offset="100%" stopColor="#18181b" />
              </linearGradient>
              <linearGradient id="m1-belt" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#4b1d1d" />
                <stop offset="100%" stopColor="#7f1d1d" />
              </linearGradient>
            </defs>

            {/* Fond intérieur voiture */}
            <rect width={800} height={700} fill="#0b1120" />
            {/* Pare-brise (vue extérieure floue) */}
            <path d="M 0 0 L 800 0 L 780 180 L 20 180 Z" fill="#1e3a5f" opacity={0.7} />
            <path d="M 50 60 L 200 40 L 600 40 L 750 60 L 720 160 L 80 160 Z" fill="#3b5f80" opacity={0.5} />

            {/* Tableau de bord */}
            <path d="M 0 180 L 800 180 L 800 300 L 0 300 Z" fill="url(#m1-dash)" />
            <path d="M 0 300 L 800 300 L 800 320 L 0 320 Z" fill="#0b1120" />

            {/* Compteurs (2 cercles) */}
            <g transform="translate(160 240)">
              <circle r={38} fill="#0b1120" stroke="#334155" strokeWidth={2} />
              <circle r={30} fill="none" stroke="#22c55e" strokeWidth={2} strokeDasharray="4 3" />
              <text y={4} textAnchor="middle" fill="#94a3b8" fontSize={9} fontFamily="monospace">km/h</text>
            </g>
            <g transform="translate(280 240)">
              <circle r={38} fill="#0b1120" stroke="#334155" strokeWidth={2} />
              <circle r={30} fill="none" stroke="#fbbf24" strokeWidth={2} strokeDasharray="4 3" />
              <text y={4} textAnchor="middle" fill="#94a3b8" fontSize={9} fontFamily="monospace">tr/min</text>
            </g>
            {/* Petit écran multifonctions */}
            <rect x={370} y={215} width={90} height={50} rx={4} fill="#0b1120" stroke="#334155" strokeWidth={1} />
            <text x={415} y={245} textAnchor="middle" fill="#22d3ee" fontSize={10} fontFamily="monospace">TRIP</text>

            {/* Grilles ventilation */}
            <g transform="translate(560 235)">
              <rect width={80} height={30} rx={4} fill="#0b1120" stroke="#334155" strokeWidth={1} />
              {[0, 1, 2, 3, 4, 5, 6].map((i) => (
                <line key={i} x1={i * 12 + 4} y1={4} x2={i * 12 + 4} y2={26} stroke="#334155" strokeWidth={1.5} />
              ))}
            </g>

            {/* HALO de surbrillance pour l'étape courante */}
            <ellipse
              cx={current.highlight.x + current.highlight.w / 2}
              cy={current.highlight.y + current.highlight.h / 2}
              rx={current.highlight.w * 0.75}
              ry={current.highlight.h * 0.75}
              fill="url(#m1-halo)"
            >
              <animate attributeName="opacity" values="0.7;1;0.7" dur="1.8s" repeatCount="indefinite" />
            </ellipse>

            {/* SIÈGE (vue 3/4 arrière) */}
            <g>
              {/* Dossier */}
              <path d="M 315 350 Q 300 220 335 130 Q 350 120 400 118 Q 450 120 465 130 Q 500 220 485 350 L 315 350 Z" fill="url(#m1-seat)" stroke="#0b1120" strokeWidth={2.5} />
              {/* Coutures verticales */}
              <line x1={360} y1={140} x2={360} y2={340} stroke="#0b1120" strokeWidth={1} opacity={0.5} />
              <line x1={440} y1={140} x2={440} y2={340} stroke="#0b1120" strokeWidth={1} opacity={0.5} />
              {/* Assise */}
              <path d="M 305 350 L 495 350 L 505 480 Q 400 500 295 480 Z" fill="url(#m1-seat)" stroke="#0b1120" strokeWidth={2.5} />
              {/* Appuie-tête */}
              <rect x={350} y={110} width={100} height={45} rx={12} fill="#1e293b" stroke="#0b1120" strokeWidth={2.5} />
              <line x1={365} y1={130} x2={365} y2={150} stroke="#374151" strokeWidth={1.5} />
              <line x1={435} y1={130} x2={435} y2={150} stroke="#374151" strokeWidth={1.5} />
              {/* Manette de réglage siège (avant) */}
              <rect x={310} y={470} width={12} height={30} rx={3} fill="#71717a" stroke="#0b1120" strokeWidth={1.5} />
            </g>

            {/* CEINTURE de sécurité */}
            <g>
              {/* Sangle épaule-hanche */}
              <path d="M 380 130 L 385 490" stroke="url(#m1-belt)" strokeWidth={12} strokeLinecap="round" opacity={0.9} />
              <path d="M 380 130 L 385 490" stroke="#7f1d1d" strokeWidth={4} strokeLinecap="round" opacity={0.5} />
              {/* Point de sortie enrouleur haut */}
              <circle cx={378} cy={120} r={5} fill="#1e293b" stroke="#0b1120" strokeWidth={1.5} />
              {/* Boucle bas */}
              <rect x={385} y={490} width={22} height={14} rx={3} fill="#374151" stroke="#0b1120" strokeWidth={1.5} />
              <rect x={396} y={492} width={4} height={10} fill="#dc2626" />
            </g>

            {/* VOLANT */}
            <g transform="translate(250 380)">
              <ellipse cx={0} cy={0} rx={95} ry={70} fill="none" stroke="url(#m1-wheel)" strokeWidth={16} />
              <ellipse cx={0} cy={0} rx={95} ry={70} fill="none" stroke="#71717a" strokeWidth={2} />
              {/* Moyeu central */}
              <ellipse cx={0} cy={0} rx={26} ry={20} fill="#18181b" stroke="#71717a" strokeWidth={2} />
              <text y={4} textAnchor="middle" fill="#71717a" fontSize={12} fontWeight="900" fontFamily="serif">CV</text>
              {/* Rayons */}
              <line x1={-26} y1={0} x2={-95} y2={0} stroke="#3f3f46" strokeWidth={5} />
              <line x1={26} y1={0} x2={95} y2={0} stroke="#3f3f46" strokeWidth={5} />
              <line x1={0} y1={20} x2={0} y2={70} stroke="#3f3f46" strokeWidth={5} />
              {/* Commandes clignotants (petit levier gauche) */}
              <rect x={-115} y={-10} width={30} height={6} rx={3} fill="#3f3f46" />
            </g>

            {/* Colonne de direction */}
            <path d="M 250 450 L 240 550 L 320 620 L 340 620 L 260 540 L 275 450 Z" fill="#1e293b" stroke="#0b1120" strokeWidth={1.5} />

            {/* Pédalier (aperçu) */}
            <g transform="translate(180 620)">
              {/* Pédale embrayage */}
              <rect x={-15} y={-14} width={22} height={28} rx={4} fill="#374151" stroke="#0b1120" strokeWidth={1.5} />
              <text x={-4} y={4} textAnchor="middle" fill="#94a3b8" fontSize={8} fontFamily="monospace">C</text>
              {/* Pédale frein */}
              <rect x={22} y={-14} width={26} height={28} rx={4} fill="#374151" stroke="#0b1120" strokeWidth={1.5} />
              <text x={35} y={4} textAnchor="middle" fill="#94a3b8" fontSize={8} fontFamily="monospace">F</text>
              {/* Pédale accélérateur */}
              <rect x={54} y={-14} width={22} height={28} rx={4} fill="#374151" stroke="#0b1120" strokeWidth={1.5} />
              <text x={65} y={4} textAnchor="middle" fill="#94a3b8" fontSize={8} fontFamily="monospace">A</text>
            </g>

            {/* Rétros — intérieur haut centre, extérieurs pare-brise gauche/droite */}
            {/* Rétro intérieur */}
            <g transform="translate(400 90)">
              <rect x={-40} y={-15} width={80} height={22} rx={4} fill="#1e293b" stroke="#334155" strokeWidth={2} />
              <rect x={-36} y={-11} width={72} height={14} rx={2} fill="#0ea5e9" opacity={0.55} />
            </g>
            {/* Support intérieur */}
            <line x1={400} y1={75} x2={400} y2={40} stroke="#374151" strokeWidth={3} />

            {/* Rétro extérieur gauche (visible côté pare-brise G) */}
            <g transform="translate(220 190)">
              <path d="M -20 -12 L 12 -8 L 18 4 L 8 12 L -20 8 Z" fill="#1e293b" stroke="#334155" strokeWidth={2} />
              <path d="M -16 -8 L 8 -5 L 12 3 L 4 8 L -16 4 Z" fill="#0ea5e9" opacity={0.55} />
            </g>
            {/* Rétro extérieur droit */}
            <g transform="translate(580 190)">
              <path d="M 20 -12 L -12 -8 L -18 4 L -8 12 L 20 8 Z" fill="#1e293b" stroke="#334155" strokeWidth={2} />
              <path d="M 16 -8 L -8 -5 L -12 3 L -4 8 L 16 4 Z" fill="#0ea5e9" opacity={0.55} />
            </g>

            {/* Rectangle de surlignage discret sur la zone active */}
            <rect
              x={current.highlight.x} y={current.highlight.y}
              width={current.highlight.w} height={current.highlight.h}
              fill="none"
              stroke={current.color}
              strokeWidth={3}
              strokeDasharray="12 8"
              rx={12}
              opacity={0.65}
            >
              <animate attributeName="stroke-dashoffset" from="0" to="-40" dur="2s" repeatCount="indefinite" />
            </rect>

            {/* Bandeau bas */}
            <g transform="translate(0, 655)">
              <rect width={800} height={45} fill="#000" opacity={0.65} />
              <text x={20} y={28} fill="#fff" fontSize={13} fontWeight="bold">M1 · Installation au poste de conduite</text>
              <text x={20} y={42} fill="#94a3b8" fontSize={11}>REMC C1b — Connaître et maîtriser son véhicule</text>
              <circle cx={770} cy={22} r={16} fill="#dc2626" />
              <text x={770} y={27} textAnchor="middle" fill="#fff" fontSize={11} fontWeight="900">CV</text>
            </g>
          </svg>

          {/* Progression bar */}
          <div className="p-4 border-t border-white/10 bg-black/40">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-white/60 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">
                Progression installation
              </span>
              <span className="font-mono-spec text-white text-[13px] font-bold">
                {done.size}<span className="text-white/40 text-[10px]">/{ETAPES.length}</span>
              </span>
            </div>
            <div className="w-full h-3 rounded-full bg-black/60 border border-white/10 overflow-hidden">
              <div
                className="h-full transition-all duration-500"
                style={{
                  width: `${(done.size / ETAPES.length) * 100}%`,
                  background: done.size === ETAPES.length ? '#22c55e' : '#f59e0b',
                }}
              />
            </div>
          </div>
        </div>

        {/* Panneau latéral */}
        <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-4 sm:p-5 self-start lg:max-h-[calc(100vh-300px)] lg:overflow-y-auto lg:custom-scroll" data-testid="m1-panel">
          <div className="rounded-xl border-2 p-3.5" style={{ borderColor: current.color, background: current.color + '18' }}>
            <div className="text-[10.5px] font-mono-spec uppercase tracking-wider font-bold flex items-center justify-between" style={{ color: current.color }}>
              <span>Étape {current.n} / {ETAPES.length}</span>
              {done.has(current.id) && (
                <span className="inline-flex items-center gap-1 text-emerald-300 text-[10px]">
                  <Check size={11} strokeWidth={3} /> Validée
                </span>
              )}
            </div>
            <div className="mt-1 font-serif-editorial text-white text-[20px] leading-tight">
              {current.label}
            </div>
          </div>

          <div className="mt-4 rounded-lg border border-cyan-500/30 bg-cyan-950/20 p-3 flex items-start gap-2">
            <Info size={14} className="text-cyan-300 mt-0.5 shrink-0" />
            <div>
              <div className="text-cyan-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">Pourquoi</div>
              <p className="mt-1 text-white/85 text-[12.5px] leading-relaxed">{current.pourquoi}</p>
            </div>
          </div>

          <div className="mt-3 rounded-lg border border-amber-500/40 bg-amber-950/20 p-3 flex items-start gap-2">
            <AlertTriangle size={14} className="text-amber-300 mt-0.5 shrink-0" />
            <div>
              <div className="text-amber-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">⚠ Piège classique</div>
              <p className="mt-1 text-white/85 text-[12.5px] leading-relaxed">{current.piege}</p>
            </div>
          </div>

          <div className="mt-3 rounded-lg border border-emerald-500/40 bg-emerald-950/20 p-3 flex items-start gap-2">
            <CheckCircle2 size={14} className="text-emerald-300 mt-0.5 shrink-0" />
            <div>
              <div className="text-emerald-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">✓ Le bon geste</div>
              <p className="mt-1 text-white/85 text-[12.5px] leading-relaxed">{current.geste}</p>
            </div>
          </div>

          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={validate}
              disabled={done.has(current.id)}
              data-testid="m1-validate"
              className="flex-1 h-10 rounded-full bg-emerald-500 text-emerald-950 font-bold text-[12.5px] hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1.5"
            >
              <Check size={14} strokeWidth={3} /> Étape maîtrisée
            </button>
            <button
              type="button"
              onClick={reset}
              data-testid="m1-reset"
              className="h-10 px-3 rounded-full border border-white/20 text-white/70 hover:bg-white/5 text-[11.5px] font-semibold inline-flex items-center gap-1"
            >
              <RotateCcw size={12} /> Reset
            </button>
          </div>

          {done.size === ETAPES.length && (
            <div className="mt-3 rounded-lg border border-emerald-500/60 bg-emerald-950/30 p-3 text-center">
              <div className="text-emerald-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">Bravo</div>
              <p className="mt-1 text-white text-[13px] font-semibold">Installation complète — tu peux démarrer 🚗</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
